import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getRegisterUrl } from '../utils/registroOrigen';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const registerUrl = location.pathname === '/summer-edition'
    ? getRegisterUrl('summer')
    : getRegisterUrl();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await logout();
      // El AuthContext ya maneja la navegación
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // En caso de error, navegar manualmente
      navigate('/login');
    }
  };

  const toggleMobileNav = () => {
    setMobileNavOpen(!mobileNavOpen);
    setUserMenuOpen(false); // Cerrar user menu al abrir mobile nav
  };

  const closeMobileNav = () => {
    setMobileNavOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Cerrar menú móvil al cambiar de ruta
  useEffect(() => {
    closeMobileNav();
  }, [location.pathname]);

  // Efecto para manejar el cambio de tamaño de la ventana y clicks fuera
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    const handleClickOutside = (event) => {
      // Cerrar user menu si se hace clic fuera de él
      if (userMenuOpen && !event.target.closest('.user-menu') && !event.target.closest('[data-user-menu]')) {
        setUserMenuOpen(false);
      }
      // Cerrar mobile nav si se hace clic fuera de él
      if (mobileNavOpen && !event.target.closest('nav') && !event.target.closest('.mobile-menu-toggle')) {
        setMobileNavOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutside);

    // Limpieza del event listener cuando el componente se desmonta
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [userMenuOpen, mobileNavOpen]);

  // Estilos inline para asegurar que el header ocupe todo el ancho disponible
  const headerStyle = {
    width: '100%',
    backgroundColor: '#061156',
    boxShadow: '0 2px 4px var(--shadow-color)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
  };

  const containerStyle = {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '15px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
  };

  // Estilos responsivos para el texto del logo
  const logoTextStyle = {
    color: '#ffffff',
    fontSize: windowWidth <= 360 ? '16px' : windowWidth <= 480 ? '18px' : windowWidth <= 768 ? '20px' : '22px',
    lineHeight: '1.2',
    display: 'flex',
    flexDirection: 'column',
  };

  // Estilo para el texto del logo
  const ligaProKidsStyle = {
    fontFamily: "'Rubik', sans-serif",
    fontWeight: '700',
    letterSpacing: '-0.5px',
  };

  // Estilos para el logo responsive
  const logoImageStyle = {
    height: windowWidth <= 360 ? '40px' : windowWidth <= 480 ? '45px' : '50px',
    width: 'auto',
    objectFit: 'contain',
    display: 'block',
    marginRight: windowWidth <= 480 ? '8px' : '10px',
  };

  return (
    <header style={headerStyle}>
      <div className="container" style={containerStyle}>
        <div className="logo" style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img
              src="/img/logos/liga-prokids-logo.png"
              alt="Liga ProKids Logo"
              style={logoImageStyle}
            />
            <div style={logoTextStyle}>
              <span style={ligaProKidsStyle}>Liga ProKids</span>
            </div>
          </Link>
        </div>

        {/* Navegación horizontal - Para todos los usuarios en desktop */}
        {windowWidth > 992 && (
          <nav style={{ display: 'flex', alignItems: 'center', gap: '30px', flex: 1, justifyContent: 'center' }}>
            <Link
              to="/liga-prokids"
              style={{
                color: isActive('/liga-prokids') ? '#FF8C00' : '#ffffff',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                padding: '8px 12px',
                borderRadius: '6px',
                transition: 'all 0.3s ease',
                opacity: isActive('/liga-prokids') ? 1 : 0.9,
              }}
              onMouseOver={(e) => {
                if (!isActive('/liga-prokids')) {
                  e.target.style.color = '#FF8C00';
                  e.target.style.backgroundColor = 'rgba(255, 140, 0, 0.1)';
                }
              }}
              onMouseOut={(e) => {
                if (!isActive('/liga-prokids')) {
                  e.target.style.color = '#ffffff';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              Liga Prokids
            </Link>

            <Link
              to="/summer-edition"
              style={{
                color: isActive('/summer-edition') ? '#FF8C00' : '#ffffff',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                padding: '8px 12px',
                borderRadius: '6px',
                transition: 'all 0.3s ease',
                opacity: isActive('/summer-edition') ? 1 : 0.9,
              }}
              onMouseOver={(e) => {
                if (!isActive('/summer-edition')) {
                  e.target.style.color = '#FF8C00';
                  e.target.style.backgroundColor = 'rgba(255, 140, 0, 0.1)';
                }
              }}
              onMouseOut={(e) => {
                if (!isActive('/summer-edition')) {
                  e.target.style.color = '#ffffff';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              Summer Edition
            </Link>

            <Link
              to="/about"
              style={{
                color: isActive('/about') ? '#FF8C00' : '#ffffff',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                padding: '8px 12px',
                borderRadius: '6px',
                transition: 'all 0.3s ease',
                opacity: isActive('/about') ? 1 : 0.9,
              }}
              onMouseOver={(e) => {
                if (!isActive('/about')) {
                  e.target.style.color = '#FF8C00';
                  e.target.style.backgroundColor = 'rgba(255, 140, 0, 0.1)';
                }
              }}
              onMouseOut={(e) => {
                if (!isActive('/about')) {
                  e.target.style.color = '#ffffff';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              Sobre Nosotros
            </Link>

            <Link
              to="/contact"
              style={{
                color: isActive('/contact') ? '#FF8C00' : '#ffffff',
                textDecoration: 'none',
                fontSize: '15px',
                fontWeight: '500',
                padding: '8px 12px',
                borderRadius: '6px',
                transition: 'all 0.3s ease',
                opacity: isActive('/contact') ? 1 : 0.9,
              }}
              onMouseOver={(e) => {
                if (!isActive('/contact')) {
                  e.target.style.color = '#FF8C00';
                  e.target.style.backgroundColor = 'rgba(255, 140, 0, 0.1)';
                }
              }}
              onMouseOut={(e) => {
                if (!isActive('/contact')) {
                  e.target.style.color = '#ffffff';
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              Contacto
            </Link>
          </nav>
        )}

        {/* Botones de autenticación - Solo para usuarios NO logueados en desktop */}
        {!user && windowWidth > 992 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Link
              to="/login"
              style={{
                backgroundColor: '#FF8C00',
                color: '#ffffff',
                textDecoration: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#FF7F00';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#FF8C00';
              }}
            >
              Iniciar Sesión
            </Link>
            <Link
              to={registerUrl}
              style={{
                backgroundColor: '#28A745',
                color: '#ffffff',
                textDecoration: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#218838';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#28A745';
              }}
            >
              Registrarse
            </Link>
          </div>
        )}

        {/* Botón Cerrar Sesión - Solo visible cuando hay usuario logueado */}
        {user && windowWidth > 992 && (
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#dc3545',
              border: 'none',
              color: '#ffffff',
              padding: '10px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#c82333';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#dc3545';
            }}
          >
            <span className="material-icons" style={{ fontSize: '18px' }}>
              logout
            </span>
            Cerrar Sesión
          </button>

        )}

        {/* Botón hamburguesa - Solo visible en mobile */}
        {windowWidth <= 992 && (
          <button
            className="mobile-menu-toggle"
            onClick={toggleMobileNav}
            aria-label={mobileNavOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={mobileNavOpen}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            color: 'var(--text-light)',
            padding: windowWidth <= 360 ? '6px' : windowWidth <= 480 ? '8px' : '12px',
            borderRadius: '8px',
            cursor: 'pointer',
            minWidth: windowWidth <= 360 ? '36px' : windowWidth <= 480 ? '40px' : '48px',
            minHeight: windowWidth <= 360 ? '36px' : windowWidth <= 480 ? '40px' : '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
          }}
        >
          <span
            className="material-icons"
            style={{
              fontSize: windowWidth <= 360 ? '22px' : windowWidth <= 480 ? '24px' : '28px',
              transition: 'transform 0.3s ease',
              transform: mobileNavOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            {mobileNavOpen ? 'close' : 'menu'}
          </span>
          </button>
        )}

        {/* Mobile Navigation - Solo visible en mobile */}
        {windowWidth <= 992 && (
          <nav
          className={mobileNavOpen ? 'nav-open' : ''}
          style={{
            backgroundColor: mobileNavOpen ? '#061156' : 'transparent',
          }}
        >
          <ul
            className={mobileNavOpen ? 'open' : ''}
            style={{
              backgroundColor: mobileNavOpen ? '#061156' : 'transparent',
              padding: mobileNavOpen ? '15px 20px' : '0',
            }}
          >
            <li>
              <Link
                to="/"
                className={isActive('/') ? 'active' : ''}
                onClick={closeMobileNav}
                style={{
                  color: 'var(--text-light)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minHeight: '48px',
                  padding: windowWidth <= 992 ? '16px 20px' : '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  ...(isActive('/') && { backgroundColor: 'rgba(255, 255, 255, 0.1)' })
                }}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>home</span>
                Inicio
              </Link>
            </li>

            <li>
              <Link
                to="/liga-prokids"
                className={isActive('/liga-prokids') ? 'active' : ''}
                onClick={closeMobileNav}
                style={{
                  color: 'var(--text-light)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minHeight: '48px',
                  padding: windowWidth <= 992 ? '16px 20px' : '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  ...(isActive('/liga-prokids') && { backgroundColor: 'rgba(255, 255, 255, 0.1)' })
                }}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>sports_soccer</span>
                Liga Prokids
              </Link>
            </li>
            <li>
              <Link
                to="/summer-edition"
                className={isActive('/summer-edition') ? 'active' : ''}
                onClick={closeMobileNav}
                style={{
                  color: 'var(--text-light)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minHeight: '48px',
                  padding: windowWidth <= 992 ? '16px 20px' : '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  ...(isActive('/summer-edition') && { backgroundColor: 'rgba(255, 255, 255, 0.1)' })
                }}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>wb_sunny</span>
                Summer Edition
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                className={isActive('/about') ? 'active' : ''}
                onClick={closeMobileNav}
                style={{
                  color: 'var(--text-light)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minHeight: '48px',
                  padding: windowWidth <= 992 ? '16px 20px' : '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  ...(isActive('/about') && { backgroundColor: 'rgba(255, 255, 255, 0.1)' })
                }}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>info</span>
                Sobre Nosotros
              </Link>
            </li>

            <li>
              <Link
                to="/contact"
                className={isActive('/contact') ? 'active' : ''}
                onClick={closeMobileNav}
                style={{
                  color: 'var(--text-light)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minHeight: '48px',
                  padding: windowWidth <= 992 ? '16px 20px' : '8px 12px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  ...(isActive('/contact') && { backgroundColor: 'rgba(255, 255, 255, 0.1)' })
                }}
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>contact_mail</span>
                Contacto
              </Link>
            </li>

            {/* Botones de autenticación - solo para usuarios no autenticados */}
            {!user && (
              <>
                <li>
                  <Link
                    to="/login"
                    onClick={closeMobileNav}
                    style={{
                      backgroundColor: '#FF8C00', // Naranja
                      color: 'var(--text-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '48px',
                      padding: windowWidth <= 992 ? '12px 16px' : '6px 10px',
                      borderRadius: '6px',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: windowWidth <= 992 ? '14px' : '13px',
                      marginTop: windowWidth <= 992 ? '8px' : '0',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#FF7F00';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#FF8C00';
                    }}
                  >
                    Iniciar Sesión
                  </Link>
                </li>

                <li>
                  <Link
                    to={registerUrl}
                    onClick={closeMobileNav}
                    style={{
                      backgroundColor: '#28A745', // Verde
                      color: 'var(--text-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '48px',
                      padding: windowWidth <= 992 ? '12px 16px' : '6px 10px',
                      borderRadius: '6px',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: windowWidth <= 992 ? '14px' : '13px',
                      marginTop: windowWidth <= 992 ? '4px' : '0',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#218838';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#28A745';
                    }}
                  >
                    Registrarse
                  </Link>
                </li>
              </>
            )}

            {/* Botón Cerrar Sesión - solo para usuarios autenticados */}
            {user && (
              <li>
                <button
                  onClick={(e) => {
                    handleLogout(e);
                    closeMobileNav();
                  }}
                  style={{
                    backgroundColor: '#dc3545', // Rojo
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minHeight: '48px',
                    padding: windowWidth <= 992 ? '12px 16px' : '6px 10px',
                    borderRadius: '6px',
                    transition: 'all 0.3s ease',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: windowWidth <= 992 ? '14px' : '13px',
                    marginTop: windowWidth <= 992 ? '8px' : '0',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#c82333';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#dc3545';
                  }}
                >
                  <span className="material-icons" style={{ fontSize: '18px' }}>
                    logout
                  </span>
                  Cerrar Sesión
                </button>
              </li>
            )}

            {user && (user.role === 'admin' || user.role === 'coach') && (
              <li>
                <Link
                  to="/admin"
                  onClick={closeMobileNav}
                  style={{
                    backgroundColor: 'var(--secondary-color)',
                    color: 'var(--text-light)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    minHeight: '48px',
                    padding: windowWidth <= 992 ? '16px 20px' : '8px 15px',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    fontWeight: '500',
                    marginTop: '8px'
                  }}
                >
                  <span className="material-icons" style={{ fontSize: '20px' }}>admin_panel_settings</span>
                  Panel Admin
                </Link>
              </li>
            )}
          </ul>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
